// src/autodesk_usage/hooks/useCompanyProductUsageByMonth.ts
// All comments in English only
import { useQuery } from "@tanstack/react-query";
import {
  getCompanyProductUsageByMonth,
  type CorporateProductUsageSummary,
} from "../api/corporate";

export interface UseCompanyProductUsageByMonthResult {
  data?: CorporateProductUsageSummary[];
  isLoading: boolean;
  error: unknown;
}

function normalizeCompanyNames(value?: string | string[] | null) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return Array.from(new Set(list.map((item) => item.trim()).filter(Boolean))).sort();
}

function normalizeCompanyProductUsagePayload(
  payload: unknown
): CorporateProductUsageSummary[] {
  if (Array.isArray(payload)) {
    return payload as CorporateProductUsageSummary[];
  }
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as any).items)
  ) {
    return (payload as any).items as CorporateProductUsageSummary[];
  }
  return [];
}

export function useCompanyProductUsageByMonth(params?: {
  companyName?: string | string[];
  month?: string;
  startMonth?: string;
  endMonth?: string;
}): UseCompanyProductUsageByMonthResult {
  const companyNames = normalizeCompanyNames(params?.companyName);
  const companyKey = companyNames.join("|");
  const query = useQuery<CorporateProductUsageSummary[]>({
    queryKey: [
      "company-product-usage",
      companyKey,
      params?.month,
      params?.startMonth,
      params?.endMonth,
    ],
    queryFn: async () => {
      if (!companyNames.length) return [];
      const res = await getCompanyProductUsageByMonth(
        companyNames,
        params.month,
        params.startMonth,
        params.endMonth
      );
      return normalizeCompanyProductUsagePayload(res.data);
    },
    enabled: companyNames.length > 0,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
